import React from 'react'
import StandardModalContent from 'GeneralUI/StandardModalContent/StandardModalContent.jsx'
import { openModal } from 'actions/modalActions'
import _ from 'lodash'

// the UI needs unique keys for every screen and window to track the components. 
// these functions will generate those values if they don't yet exist (during initial load)
// and during any update and then remove them before saving to the api
export const generateUniqueWindowKeys = (window) => {
  const updatedWindow = _.cloneDeep(window)
  if (!updatedWindow.key) {
    updatedWindow.key = _.uniqueId('window-')
  }
  return updatedWindow
}

// the UI needs unique keys for every screen and window to track the components. 
// these functions will generate those values if they don't yet exist (during initial load)
// and during any update and then remove them before saving to the api
export const generateUniqueScreenKeys = (screen) => {
  const updatedScreen = _.cloneDeep(screen)
  if (!updatedScreen.key) {
    updatedScreen.key = _.uniqueId('screen-')
  }
  updatedScreen.windows = (updatedScreen.windows || []).map(window => generateUniqueWindowKeys(window))
  return updatedScreen
}

export const generateUniqueWorkflowKeys = (w, type) => {
  const updatedWorkflow = _.cloneDeep(w)
  if (!updatedWorkflow.key)
    updatedWorkflow.key = _.uniqueId(`${type}-`)
  return updatedWorkflow
}

export const removeWindowKeys = (window) => {
  const updatedWindow = _.cloneDeep(window)
  delete updatedWindow.key
  return updatedWindow
}

export const removeScreenKeys = (screen) => {
  const updatedScreen = _.cloneDeep(screen)
  delete updatedScreen.key
  updatedScreen.windows = (updatedScreen.windows || []).map(window => removeWindowKeys(window))
  return updatedScreen
}

export const removeWorkflowKeys = (workflow) => {
  //functionally the same as remove window keys
  return removeWindowKeys(workflow)
}

export const getNextAvailablePath = (screens, prefix) => {
  const pathsInUse = screens.map(screen => screen.path)
  let nextPath = '';
  for (let i = 1; i < 100; i++) {
    nextPath = `new_${prefix}_${i}`
    // we found a good path so stop searching
    if (!pathsInUse.includes(nextPath))
      break
  }
  // technically its possible to get here without finding a next available path in which case
  // we would keep reusing "new_screen_100" but thats pretty unlikely.
  return nextPath;
}

export const getErrorModalAction = (title, message) => {
  return openModal('error_modal', {
    title,
    content: (
      <StandardModalContent
        messageContent={message}
        okHandler={() => null}
        okText={'OK'}
      />
    ),
    closeable: true
  })
}

export const percentToPx = (percentValue, offset, max) => {
  if (typeof percentValue === "string" || percentValue instanceof String) {
    percentValue = percentValue.replace("%", "")
  }
  // example container = 100px vertical offset = 10px window position = 55px)
  // for the example this means the available window area is 100px - 10px = 90px (with the 10px
  // offset above that) so if they window is at 50% of the 90 its at 45px + the 10px offset so 55px

  // unshifted/offset position in pixes would just be percent of max.
  // using example values unshiftedVerticalOffsetPx = (50%/100) * 100px = 50px
  const unshiftedPositionPx = (percentValue / 100) * max

  // to apply the correct offset we need the scale factor. A window at the top of the screen would
  // get the full offset while as window at the bottom would get 0 offset and a window in the middle
  // would get 50% offset
  // example values = 1 - 50% / 100 = .5 scaleFactor
  const scaleFactor = 1 - percentValue / 100

  // add the scaled offset value to the unshifted position
  // using example values = 50px + .5 * 10px = 55px
  const shiftedPositionPx = unshiftedPositionPx + scaleFactor * offset
  return shiftedPositionPx
}

// this is used for converting the current px based position to a % value for the adjust window modal below.
// again, this is designed to work for height and width but comments provided are for height only
export const pxToPercent = (positionPx, offset, max) => {
  // example container = 100px vertical offset = 10px window position = 55px (aka 50%, see explanation in percentToPx)

  // scale amount is 1 - the unshifted window position in the scaled container
  // scaleAmount = 1 - (positionPx - verticalOffset) / scaled vertical height
  // example scaleAmount = 1 - ((55px - 10px) / (100px - 90px)) = .5
  const scaleAmount = 1 - (positionPx - offset) / (max - offset)
  // now we need to unshift the window by the scaled vertical offset
  // example unshiftedVerticalOffsetPx = 55px - (.5 * 10px) = 50px
  const unshiftedPositionPx = positionPx - scaleAmount * offset
  // the percentValue = the unshiftedVerticalPosition / fullContainerHeight
  // example 100 * (50px / 100px) = 50%
  const percentValue = 100 * (unshiftedPositionPx / max)
  return Math.round(percentValue)
}

export const windowConfigCleaner = (windowList = [], systemWindows) => {
  return windowList
    .map(newWindow => {
      newWindow.selectable = true // show window in windows list
      delete newWindow.modalCapable // in old configs but not used anymore. we now use modalConfig
      const windowConfig = systemWindows.find(config => {
        return config.id === newWindow.ref
      })
      // if this window supports being displayed as a modal, copy its modalConfig into the window definition.
      if (windowConfig?.modalConfig) {
        newWindow.modalConfig = windowConfig.modalConfig
      }

      // remove standard window information that doesn't belong in configs
      // but gets added when windowConfigs are actually in use or being
      // created from raw systemWindows definitions.
      delete newWindow.zIndex
      delete newWindow.contexts
      delete newWindow.context
      delete newWindow.path
      return newWindow
    }).map(removeWindowKeys)
}

// this can be used for windows, screens, or workflows
export const addScreenType = (item) => {
  if (!item.screenType) {
    item.screenType = 'standard'
  }
  return item
}